/**
 * Custom Subscription Picker JavaScript
 * Handles interactions between subscription options and variant selection
 */

class CustomSubscriptionPicker {
  constructor(container) {
    this.container = container;
    this.sectionId = container.dataset.sectionId;
    this.productData = this.getProductData();
    this.currentVariant = null;
    this.currentSellingPlan = null;
    this.currentPurchaseType = 'one-time';
    this.subscriptionDiscount = 0;
    this.sellingPlanGroupId = null;
    
    this.init();
  }
  
  getProductData() {
    const script = document.querySelector('script[data-product-json]');
    if (script) {
      return JSON.parse(script.textContent);
    }
    
    // Fallback: get data from variant selects if available
    const variantSelects = document.querySelector('variant-selects');
    if (variantSelects) {
      const selectedVariantScript = variantSelects.querySelector('script[data-selected-variant]');
      if (selectedVariantScript) {
        return JSON.parse(selectedVariantScript.textContent);
      }
    }
    
    return null;
  }
  
  init() {
    // Set initial variant from the selected variant element
    this.setInitialVariant();
    
    this.bindEvents();
    this.updatePrices();
    this.updateHiddenInputs();
    this.syncWithExistingVariantPicker();
    this.moveSellingPlanInputToForm();
  }
  
  setInitialVariant() {
    // Find the initially selected variant element
    const selectedVariantElement = this.container.querySelector('.variant-option--selected');
    if (selectedVariantElement) {
      const variantId = selectedVariantElement.dataset.variantId;
      const variantPrice = selectedVariantElement.dataset.price;
      const variantComparePrice = selectedVariantElement.dataset.comparePrice;
      
      // Always try to get subscription price from the subscription section
      const subscriptionVariantOption = this.container.querySelector(
        `.variant-options[data-purchase-type="subscription"] [data-variant-id="${variantId}"]`
      );
      
      let subscriptionPrice = null;
      if (subscriptionVariantOption) {
        subscriptionPrice = subscriptionVariantOption.dataset.subscriptionPrice;
      }
      
      this.currentVariant = {
        id: parseInt(variantId),
        price: parseInt(variantPrice),
        compare_at_price: variantComparePrice ? parseInt(variantComparePrice) : null,
        subscription_price: subscriptionPrice ? parseInt(subscriptionPrice) : null
      };
      
      console.log('setInitialVariant - currentVariant:', this.currentVariant);
      
      // Ensure the variant selection is properly set
      this.updateVariantSelection(selectedVariantElement);
    }
    
    // Get subscription discount from the subscription option
    const subscriptionOption = this.container.querySelector('[data-option-type="subscription"]');
    if (subscriptionOption) {
      this.subscriptionDiscount = parseInt(subscriptionOption.dataset.subscriptionDiscount) || 0;
      this.sellingPlanGroupId = subscriptionOption.dataset.sellingPlanGroupId;
    }
  }
  
  bindEvents() {
    // Purchase type radio buttons
    const purchaseTypeRadios = this.container.querySelectorAll('input[name="purchase_type"]');
    purchaseTypeRadios.forEach(radio => {
      radio.addEventListener('change', (e) => {
        this.handlePurchaseTypeChange(e.target.value);
      });
    });
    
    // Variant options
    const variantOptions = this.container.querySelectorAll('.variant-option');
    variantOptions.forEach(option => {
      option.addEventListener('click', (e) => {
        this.handleVariantChange(e.currentTarget);
      });
    });
    
    // Frequency options
    const frequencyOptions = this.container.querySelectorAll('.frequency-option');
    frequencyOptions.forEach(option => {
      option.addEventListener('click', (e) => {
        this.handleFrequencyChange(e.currentTarget);
      });
    });
    
    // Listen for external variant changes (from other variant pickers)
    document.addEventListener('variant:change', (e) => {
      this.handleExternalVariantChange(e.detail);
    });
  }
  
  handlePurchaseTypeChange(purchaseType) {
    this.currentPurchaseType = purchaseType;
    
    console.log('handlePurchaseTypeChange:', purchaseType);
    
    // Update UI visibility
    this.updatePurchaseTypeUI();
    
    // Reset selling plan for one-time purchases
    if (purchaseType === 'one-time') {
      this.currentSellingPlan = null;
    } else {
      // Set default selling plan for subscription
      let selectedFrequencyOption = this.container.querySelector('.frequency-option--selected');
      
      // If no option is selected, select the first one
      if (!selectedFrequencyOption) {
        selectedFrequencyOption = this.container.querySelector('.frequency-option');
        if (selectedFrequencyOption) {
          // Update visual selection
          this.updateFrequencySelection(selectedFrequencyOption);
        }
      }
      
      // Set the selling plan
      if (selectedFrequencyOption) {
        const sellingPlanId = selectedFrequencyOption.dataset.sellingPlanId;
        this.currentSellingPlan = sellingPlanId;
        console.log('handlePurchaseTypeChange - Set default selling plan:', sellingPlanId);
      } else {
        console.warn('handlePurchaseTypeChange - No frequency option found!');
      }
      
      // When switching to subscription, reload variant data to get subscription_price
      if (this.currentVariant) {
        const subscriptionVariantOption = this.container.querySelector(
          `.variant-options[data-purchase-type="subscription"] [data-variant-id="${this.currentVariant.id}"]`
        );
        
        if (subscriptionVariantOption) {
          const subscriptionPrice = subscriptionVariantOption.dataset.subscriptionPrice;
          console.log('Switching to subscription - loading subscription_price:', subscriptionPrice);
          
          // Update current variant with subscription price
          this.currentVariant.subscription_price = subscriptionPrice ? parseInt(subscriptionPrice) : null;
          
          console.log('Updated currentVariant:', this.currentVariant);
        }
      }
    }
    
    this.updatePrices();
    this.updateHiddenInputs();
  }
  
  handleVariantChange(variantElement) {
    const variantId = variantElement.dataset.variantId;
    const variantPrice = variantElement.dataset.price;
    const variantComparePrice = variantElement.dataset.comparePrice;
    const subscriptionPrice = variantElement.dataset.subscriptionPrice;
    
    console.log('handleVariantChange - Reading data attributes:', {
      variantId,
      variantPrice,
      variantComparePrice,
      subscriptionPrice
    });
    
    // Update selected state
    this.updateVariantSelection(variantElement);
    
    // Create variant object from the element data
    this.currentVariant = {
      id: parseInt(variantId),
      price: parseInt(variantPrice),
      compare_at_price: variantComparePrice ? parseInt(variantComparePrice) : null,
      subscription_price: subscriptionPrice ? parseInt(subscriptionPrice) : null
    };
    
    console.log('handleVariantChange - currentVariant:', this.currentVariant);
    
    // Update the existing product form's variant input
    this.updateExistingVariantInput(variantId);
    
    this.updatePrices();
    this.updateHiddenInputs();
    
    // Dispatch variant change event
    this.dispatchVariantChangeEvent();
  }
  
  handleFrequencyChange(frequencyElement) {
    const sellingPlanId = frequencyElement.dataset.sellingPlanId;
    
    // Update selected state
    this.updateFrequencySelection(frequencyElement);
    
    // Update current selling plan
    this.currentSellingPlan = sellingPlanId;
    
    this.updatePrices();
    this.updateHiddenInputs();
  }
  
  handleExternalVariantChange(variantData) {
    // Update variant selection when changed from external picker
    const variantId = variantData.id || variantData;
    
    console.log('handleExternalVariantChange - Triggered by external source, variantId:', variantId);
    
    // Apply selection to ALL matching options inside this picker
    const matchingOptions = this.container.querySelectorAll(`[data-variant-id="${variantId}"]`);
    
    if (matchingOptions && matchingOptions.length > 0) {
      // Remove selected class from all variant options first
      const variantOptions = this.container.querySelectorAll('.variant-option');
      variantOptions.forEach(option => option.classList.remove('variant-option--selected'));
      // Add to all matches
      matchingOptions.forEach(option => option.classList.add('variant-option--selected'));
      
      // Find the subscription variant option (in subscription section) to get correct subscription_price
      const subscriptionVariantOption = this.container.querySelector(
        `.variant-options[data-purchase-type="subscription"] [data-variant-id="${variantId}"]`
      );
      
      if (subscriptionVariantOption) {
        const variantPrice = subscriptionVariantOption.dataset.price;
        const variantComparePrice = subscriptionVariantOption.dataset.comparePrice;
        const subscriptionPrice = subscriptionVariantOption.dataset.subscriptionPrice;
        
        console.log('handleExternalVariantChange - Reading from subscription variant:', {
          variantPrice,
          variantComparePrice,
          subscriptionPrice
        });
        
        this.currentVariant = {
          id: parseInt(variantId),
          price: parseInt(variantPrice),
          compare_at_price: variantComparePrice ? parseInt(variantComparePrice) : null,
          subscription_price: subscriptionPrice ? parseInt(subscriptionPrice) : null
        };
        
        console.log('handleExternalVariantChange - Updated currentVariant:', this.currentVariant);
      } else {
        // Fallback: use first matching option
        const referenceElement = matchingOptions[0];
        const variantPrice = referenceElement.dataset.price;
        const variantComparePrice = referenceElement.dataset.comparePrice;
        const subscriptionPrice = referenceElement.dataset.subscriptionPrice;
        
        this.currentVariant = {
          id: parseInt(variantId),
          price: parseInt(variantPrice),
          compare_at_price: variantComparePrice ? parseInt(variantComparePrice) : null,
          subscription_price: subscriptionPrice ? parseInt(subscriptionPrice) : null
        };
      }
      
      this.updatePrices();
    }
  }
  
  updatePurchaseTypeUI() {
    // Hide all variant and frequency options
    const allOptions = this.container.querySelectorAll('.variant-options, .frequency-options');
    allOptions.forEach(option => {
      option.style.display = 'none';
    });
    
    // Show relevant options for selected purchase type
    const selectedPurchaseType = this.container.querySelector('input[name="purchase_type"]:checked').value;
    const relevantOptions = this.container.querySelectorAll(`[data-purchase-type="${selectedPurchaseType}"]`);
    relevantOptions.forEach(option => {
      option.style.display = selectedPurchaseType === 'subscription' ? 'flex' : 'grid';
    });
    
    // Show frequency options only for subscription
    if (selectedPurchaseType === 'subscription') {
      const frequencyOptions = this.container.querySelector('.frequency-options');
      if (frequencyOptions) {
        frequencyOptions.style.display = 'block';
      }
    }
  }
  
  updateVariantSelection(selectedElement) {
    // Remove selected class from all variant options
    const variantOptions = this.container.querySelectorAll('.variant-option');
    variantOptions.forEach(option => {
      option.classList.remove('variant-option--selected');
    });
    
    // Add selected class to ALL elements representing the same variant id
    const variantId = selectedElement.dataset.variantId;
    const matchingOptions = this.container.querySelectorAll(`[data-variant-id="${variantId}"]`);
    matchingOptions.forEach(option => {
      option.classList.add('variant-option--selected');
    });
  }
  
  updateFrequencySelection(selectedElement) {
    // Remove selected class from all frequency options
    const frequencyOptions = this.container.querySelectorAll('.frequency-option');
    frequencyOptions.forEach(option => {
      option.classList.remove('frequency-option--selected');
    });
    
    // Add selected class to clicked element
    selectedElement.classList.add('frequency-option--selected');
  }
  
  updatePrices() {
    if (!this.currentVariant) {
      console.log('updatePrices - No current variant');
      return;
    }
    
    console.log('updatePrices - currentVariant:', this.currentVariant);
    console.log('updatePrices - currentPurchaseType:', this.currentPurchaseType);
    console.log('updatePrices - currentSellingPlan:', this.currentSellingPlan);
    
    const variantId = this.currentVariant.id;
    const purchaseType = this.currentPurchaseType;
    
    // Update one-time purchase price
    const oneTimePriceElement = this.container.querySelector('[data-one-time-price]');
    if (oneTimePriceElement) {
      oneTimePriceElement.innerHTML = this.formatMoney(this.currentVariant.price);
    }
    
    // Update subscription price if applicable
    if (purchaseType === 'subscription' && this.currentSellingPlan) {
      this.updateSubscriptionPrice();
    }
    
    // DON'T update variant option prices - they're already correct in the HTML from Liquid
    // The variant options show subscription prices in subscription section, regular prices in one-time section
    // We only need to update the main product price display
    
    // Update main product price blocks
    this.updateMainProductPrices();
  }
  
  updateSubscriptionPrice() {
    const subscriptionPriceElement = this.container.querySelector('[data-subscription-price]');
    if (subscriptionPriceElement && this.currentVariant) {
      // Use the subscription price that was calculated in Liquid (from data attribute)
      let subscriptionPrice = this.currentVariant.subscription_price || this.currentVariant.price;
      
      console.log('updateSubscriptionPrice - Using:', {
        subscription_price: this.currentVariant.subscription_price,
        final_price: subscriptionPrice
      });
      
      // Determine the base price for comparison (use compare_at_price if available)
      let basePriceForDiscount = this.currentVariant.price;
      if (this.currentVariant.compare_at_price && this.currentVariant.compare_at_price > this.currentVariant.price) {
        basePriceForDiscount = this.currentVariant.compare_at_price;
      }
      
      subscriptionPriceElement.innerHTML = `
        ${this.formatMoney(subscriptionPrice)}
        <span class="subscription-option__original-price">${this.formatMoney(basePriceForDiscount)}</span>
      `;
    }
  }
  
  findSellingPlanAllocation(variantId, sellingPlanId) {
    // This would need to be implemented based on your product's selling plan allocations
    // For now, return null to use the fallback calculation
    return null;
  }
  
  updateMainProductPrices() {
    if (!this.currentVariant) return;
    
    const hasComparePrice = this.currentVariant.compare_at_price && 
                           this.currentVariant.compare_at_price > this.currentVariant.price;
    
    // Calculate subscription price if subscription is selected
    const isSubscriptionSelected = this.currentPurchaseType === 'subscription' && this.currentSellingPlan;
    let displayPrice = this.currentVariant.price;
    let basePriceForComparison = this.currentVariant.price;
    
    if (isSubscriptionSelected) {
      // CRITICAL: Use the subscription price that was calculated in Liquid (from data attribute)
      if (this.currentVariant.subscription_price) {
        displayPrice = this.currentVariant.subscription_price;
      } else {
        // Fallback: calculate if not available
        console.warn('subscription_price not found in currentVariant, this should not happen!');
        displayPrice = this.currentVariant.price;
      }
      
      console.log('updateMainProductPrices - Subscription selected:', {
        subscription_price: this.currentVariant.subscription_price,
        regular_price: this.currentVariant.price,
        displayPrice: displayPrice,
        formatted: this.formatMoney(displayPrice)
      });
      
      // Base price should be compare_at_price if it exists, otherwise regular price
      if (hasComparePrice) {
        basePriceForComparison = this.currentVariant.compare_at_price;
      } else {
        basePriceForComparison = this.currentVariant.price;
      }
    } else if (hasComparePrice) {
      basePriceForComparison = this.currentVariant.compare_at_price;
    }
    
    // Helper function to check if element is inside cart drawer
    const isInCartDrawer = (element) => {
      return element.closest('cart-drawer') || 
             element.closest('.cart-drawer') || 
             element.closest('#CartDrawer') ||
             element.closest('[id^="CartDrawer"]');
    };
    
    // Update regular price elements (main price)
    const regularPriceElements = document.querySelectorAll('.f-price-item--regular, .price-item--regular');
    
    regularPriceElements.forEach(element => {
      if (this.container.contains(element)) return;
      if (isInCartDrawer(element)) return; // Skip cart drawer elements

      // Only update if it's not a strikethrough element (compare price)
      if (element.tagName !== 'S') {
        // Check if this element is inside a sale container - if so, it should show the compare price
        const isInSaleContainer = element.closest('.f-price__sale, .price__sale');
  
        if (isInSaleContainer) {
          // This is the "regular price" inside a sale container - show base price (strikethrough)
          if (isSubscriptionSelected || hasComparePrice) {
            element.innerHTML = this.formatMoney(basePriceForComparison);
          } else {
            element.innerHTML = this.formatMoney(this.currentVariant.price);
          }
        } else {
          // This is a regular price element outside sale container
          // When subscription is selected, the CSS will hide this via .f-price--on-sale
          // But we should still update it in case CSS doesn't apply or as fallback
          if (isSubscriptionSelected) {
            // When subscription selected, sale container should be visible, but update this as fallback
            element.innerHTML = this.formatMoney(displayPrice);
          } else {
            // Show regular price when no subscription
            element.innerHTML = this.formatMoney(this.currentVariant.price);
          }
        }
      }
    });
    
    // Update sale price elements
    const salePriceElements = document.querySelectorAll('.f-price-item--sale, .price-item--sale');
    
    salePriceElements.forEach(element => {
      if (this.container.contains(element)) return;
      if (isInCartDrawer(element)) return; // Skip cart drawer elements
      
      // Show the display price (subscription price when subscription selected, otherwise regular price)
      element.innerHTML = this.formatMoney(displayPrice);
    });
    
    // Update elements with data-price attribute
    const dataPriceElements = document.querySelectorAll('[data-price]');
    
    dataPriceElements.forEach(element => {
      if (this.container.contains(element)) return;
      if (isInCartDrawer(element)) return; // Skip cart drawer elements
      element.innerHTML = this.formatMoney(displayPrice);
    });
    
    // Handle compare at price elements (strikethrough prices)
    // Based on the HTML structure: <span class="f-price-item--regular"><s>Dhs. 8.19</s></span>
    const compareAtPriceElements = document.querySelectorAll('.f-price-item--regular s, .price-item--regular s');
    
    compareAtPriceElements.forEach(element => {
      if (this.container.contains(element)) return;
      if (isInCartDrawer(element)) return; // Skip cart drawer elements
      
      if (isSubscriptionSelected || hasComparePrice) {
        element.innerHTML = this.formatMoney(basePriceForComparison);
        element.style.display = '';
      } else {
        element.style.display = 'none';
      }
    });
    
    // Handle price containers that show/hide based on sale status
    // The theme uses CSS to hide/show containers based on .f-price--on-sale class
    // So we need to add/remove this class on the .f-price container
    
    const priceContainers = document.querySelectorAll('.f-price, .price');
    
    const shouldShowSaleContainer = hasComparePrice || isSubscriptionSelected;
    
    priceContainers.forEach(priceContainer => {
      if (this.container.contains(priceContainer)) return;
      if (isInCartDrawer(priceContainer)) return; // Skip cart drawer elements
      
      // Add or remove the .f-price--on-sale class to trigger CSS visibility rules
      if (shouldShowSaleContainer) {
        priceContainer.classList.add('f-price--on-sale');
      } else {
        // Only remove if there's no actual compare price
        // (if compare_at_price exists, Liquid should have added it)
        if (!hasComparePrice) {
          priceContainer.classList.remove('f-price--on-sale');
        }
      }
    });
    
    // Also handle individual containers with inline styles as fallback
    const priceRegularContainers = document.querySelectorAll('.f-price__regular, .price__regular');
    const priceSaleContainers = document.querySelectorAll('.f-price__sale, .price__sale');
    
    priceRegularContainers.forEach(container => {
      if (this.container.contains(container)) return;
      if (isInCartDrawer(container)) return; // Skip cart drawer elements
      // CSS handles visibility, but ensure it's not forced hidden
      if (!shouldShowSaleContainer) {
        container.style.display = '';
      }
    });
    
    priceSaleContainers.forEach(container => {
      if (this.container.contains(container)) return;
      if (isInCartDrawer(container)) return; // Skip cart drawer elements
      // CSS handles visibility, but ensure it's shown when needed
      if (shouldShowSaleContainer) {
        container.style.display = '';
      }
    });
    
    // Update discount badges with [data-sale-value]
    const saleBadges = document.querySelectorAll('.f-badge--sale');
    
    saleBadges.forEach(badge => {
      if (this.container.contains(badge)) return;
      if (isInCartDrawer(badge)) return; // Skip cart drawer elements
      
      const saleValueElement = badge.querySelector('[data-sale-value]');
      if (!saleValueElement) return;
      
      // Determine if we should show the badge
      const shouldShowBadge = hasComparePrice || isSubscriptionSelected;
      
      if (shouldShowBadge && basePriceForComparison > displayPrice) {
        // Calculate discount
        const discountAmount = basePriceForComparison - displayPrice;
        const discountPercentage = Math.floor((discountAmount / basePriceForComparison) * 100);
        
        // Determine badge type by checking the current content
        const badgeText = badge.textContent.toLowerCase();
        
        if (badgeText.includes('%')) {
          // Percentage badge type
          saleValueElement.textContent = discountPercentage + '%';
        } else if (badgeText.match(/\d/) && !badgeText.includes('%')) {
          // Fixed amount badge type (contains numbers but not percentage)
          saleValueElement.innerHTML = this.formatMoney(discountAmount);
        }
        
        // Show the badge
        badge.style.display = '';
      } else {
        // Hide the badge when there's no sale
        badge.style.display = 'none';
      }
    });
    
    // Dispatch a custom event to notify other components about price updates
    const priceUpdateEvent = new CustomEvent('custom-subscription-price-update', {
      detail: {
        variant: this.currentVariant,
        price: displayPrice,
        compareAtPrice: basePriceForComparison,
        formattedPrice: this.formatMoney(displayPrice),
        hasComparePrice: hasComparePrice || isSubscriptionSelected,
        isSubscription: isSubscriptionSelected
      }
    });
    document.dispatchEvent(priceUpdateEvent);
  }
  
  
  updateHiddenInputs() {
    // Update selling plan ID only (variant ID is handled by existing form)
    let sellingPlanInput = this.getSellingPlanInput();
    
    // If input doesn't exist, create it
    if (!sellingPlanInput) {
      const productFormId = `product-form-${this.sectionId}`;
      let productForm = document.getElementById(productFormId);
      
      if (!productForm) {
        productForm = document.querySelector('form[action*="/cart/add"]');
      }
      
      if (productForm) {
        sellingPlanInput = document.createElement('input');
        sellingPlanInput.type = 'hidden';
        sellingPlanInput.name = 'selling_plan';
        sellingPlanInput.value = '';
        sellingPlanInput.className = 'selected-selling-plan-id';
        productForm.appendChild(sellingPlanInput);
        console.log('Created selling plan input in form');
      }
    }
    
    if (sellingPlanInput) {
      const newValue = this.currentPurchaseType === 'subscription' ? (this.currentSellingPlan || '') : '';
      
      // CRITICAL: Set value and ensure input is enabled
      sellingPlanInput.value = newValue;
      sellingPlanInput.disabled = false;
      sellingPlanInput.removeAttribute('disabled');
      sellingPlanInput.removeAttribute('readonly');
      
      // Ensure the input is visible to FormData (not hidden via CSS)
      sellingPlanInput.style.display = '';
      
      console.log('updateHiddenInputs - Updated selling plan:', {
        value: newValue,
        purchaseType: this.currentPurchaseType,
        sellingPlan: this.currentSellingPlan,
        inputFound: true,
        inputDisabled: sellingPlanInput.disabled,
        inputValue: sellingPlanInput.value
      });
      
      // Also dispatch a change event to ensure any listeners are notified
      sellingPlanInput.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      console.warn('updateHiddenInputs - Could not find or create selling plan input');
    }
  }
  
  getSellingPlanInput() {
    // Find the selling plan input in the product form (from buy-buttons.liquid)
    const productFormId = `product-form-${this.sectionId}`;
    let productForm = document.getElementById(productFormId);
    
    if (!productForm) {
      productForm = document.querySelector('form[action*="/cart/add"]');
    }
    
    if (productForm) {
      // Look for the input with the class first, then fallback to name
      let sellingPlanInput = productForm.querySelector('.selected-selling-plan-id');
      if (!sellingPlanInput) {
        sellingPlanInput = productForm.querySelector('input[name="selling_plan"]');
      }
      return sellingPlanInput;
    }
    
    return null;
  }
  
  moveSellingPlanInputToForm() {
    // Find the product form
    const productFormId = `product-form-${this.sectionId}`;
    let productForm = document.getElementById(productFormId);
    
    if (!productForm) {
      // Try alternative form selectors
      productForm = document.querySelector('form[action*="/cart/add"]');
    }
    
    if (!productForm) {
      console.warn('Product form not found for selling plan input');
      return;
    }
    
    // Find the selling plan input in the form (from buy-buttons.liquid)
    let sellingPlanInput = productForm.querySelector('.selected-selling-plan-id');
    
    // If it doesn't exist, create it
    if (!sellingPlanInput) {
      sellingPlanInput = document.createElement('input');
      sellingPlanInput.type = 'hidden';
      sellingPlanInput.name = 'selling_plan';
      sellingPlanInput.value = '';
      sellingPlanInput.className = 'selected-selling-plan-id';
      productForm.appendChild(sellingPlanInput);
      console.log('Created selling plan input in form');
    }
    
    // Add form submission debugging
    this.addFormSubmissionDebugging(productForm);
  }
  
  addFormSubmissionDebugging(productForm) {
    // CRITICAL: Listen for form submission in CAPTURE phase (before other handlers)
    // This ensures we set the selling plan value BEFORE FormData is created
    productForm.addEventListener('submit', (e) => {
      // Update hidden inputs to ensure selling plan is set
      this.updateHiddenInputs();
      
      const sellingPlanInput = productForm.querySelector('input[name="selling_plan"]');
      
      console.log('Form submission intercepted - current selling plan:', {
        inputFound: !!sellingPlanInput,
        currentValue: sellingPlanInput?.value,
        shouldBe: this.currentSellingPlan,
        purchaseType: this.currentPurchaseType
      });
      
      // Double-check: Always update the selling plan input before form submission
      if (sellingPlanInput) {
        const newValue = this.currentPurchaseType === 'subscription' ? (this.currentSellingPlan || '') : '';
        sellingPlanInput.value = newValue;
        
        console.log('Updated selling plan input:', {
          oldValue: sellingPlanInput.value,
          newValue: newValue,
          finalValue: sellingPlanInput.value
        });
        
        // Also ensure the input is not disabled
        sellingPlanInput.disabled = false;
        sellingPlanInput.removeAttribute('disabled');
      } else {
        console.warn('Selling plan input not found in form!');
      }
    }, true); // Use capture phase to run BEFORE ProductForm handler
    
    // Also listen for any clicks on the submit button as backup
    const submitButton = productForm.querySelector('[type="submit"]');
    if (submitButton) {
      submitButton.addEventListener('click', (e) => {
        // Ensure selling plan is set before submission
        const sellingPlanInput = productForm.querySelector('input[name="selling_plan"]');
        if (sellingPlanInput) {
          const newValue = this.currentPurchaseType === 'subscription' ? (this.currentSellingPlan || '') : '';
          sellingPlanInput.value = newValue;
          sellingPlanInput.disabled = false;
          sellingPlanInput.removeAttribute('disabled');
        }
      }, true); // Use capture phase
    }
    
    // Also listen for the handleFormSubmit event if it's dispatched
    document.addEventListener('handleFormSubmit', (e) => {
      const sellingPlanInput = productForm.querySelector('input[name="selling_plan"]');
      if (sellingPlanInput) {
        const newValue = this.currentPurchaseType === 'subscription' ? (this.currentSellingPlan || '') : '';
        sellingPlanInput.value = newValue;
      }
    });
  }
  
  updateExistingVariantInput(variantId) {
    // Find the existing variant input in the product form
    const productFormId = `product-form-${this.sectionId}`;
    let productForm = document.getElementById(productFormId);
    
    if (!productForm) {
      // Try alternative form selectors
      productForm = document.querySelector('form[action*="/cart/add"]');
    }
    
    if (!productForm) {
      console.warn('Product form not found for variant update');
      return;
    }
    
    // Look for existing variant input
    let variantInput = productForm.querySelector('input[name="id"]');
    if (!variantInput) {
      // Create variant input if it doesn't exist
      variantInput = document.createElement('input');
      variantInput.type = 'hidden';
      variantInput.name = 'id';
      productForm.appendChild(variantInput);
    }
    
    // Update the variant ID
    variantInput.value = variantId;
    
    // Also trigger any existing variant change events
    const variantChangeEvent = new CustomEvent('variant:change', {
      detail: { id: variantId }
    });
    document.dispatchEvent(variantChangeEvent);
    
    // Trigger change event on the input itself
    variantInput.dispatchEvent(new Event('change', { bubbles: true }));
  }
  
  syncWithExistingVariantPicker() {
    // Listen for changes from the existing variant picker
    document.addEventListener('variant:change', (e) => {
      if (e.detail && e.detail.id) {
        this.handleExternalVariantChange(e.detail);
      }
    });
    
    // Also listen for the theme's variant change events
    document.addEventListener('change', (e) => {
      if (e.target.name === 'id' && e.target.type === 'hidden') {
        const variantId = e.target.value;
        if (variantId && variantId !== this.currentVariant?.id) {
          this.handleExternalVariantChange({ id: variantId });
        }
      }
    });
  }
  
  dispatchVariantChangeEvent() {
    const event = new CustomEvent('variant:change', {
      detail: this.currentVariant
    });
    document.dispatchEvent(event);
  }
  
  formatMoney(cents) {
    // Handle invalid or missing cents
    if (!cents || isNaN(cents) || cents === null || cents === undefined) {
      return '0.00';
    }
    
    // Use theme's currency formatting system
    if (window.FoxTheme && window.FoxTheme.Currency && window.FoxTheme.Currency.formatMoney && window.FoxTheme.settings && window.FoxTheme.settings.moneyFormat) { 
      return window.FoxTheme.Currency.formatMoney(cents, window.FoxTheme.settings.moneyFormat);
    }
    
    // Fallback to Shopify's formatMoney if available
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents);
    }
    
    // Final fallback formatting - don't hardcode currency symbol
    return '<span class="aed-symbol">D</span>'+(cents / 100).toFixed(2);
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  const subscriptionPickers = document.querySelectorAll('.custom-subscription-picker');
  subscriptionPickers.forEach(picker => {
    new CustomSubscriptionPicker(picker);
  });
});

// Also initialize on section:load for dynamic loading
document.addEventListener('shopify:section:load', function(e) {
  const subscriptionPickers = e.target.querySelectorAll('.custom-subscription-picker');
  subscriptionPickers.forEach(picker => {
    new CustomSubscriptionPicker(picker);
  });
});
